import React, { useMemo } from 'react';

import type { ColumnDef } from '@tanstack/react-table';
import { useGetDepartment } from '@vlabs/api/artemy/client';
import type { DepartmentId, List } from '@vlabs/api/artemy/model';
import { DuplicateIcon } from '@vlabs/icons';
import { GoToListButton } from '@vlabs/monokit/features/go-to-list';
import { ShowCardsCountButtonPoint3 } from '@vlabs/monokit/features/show-cards-count';
import { TableView, TableViewSelectable } from '@vlabs/monokit/shared/ui/table-view';
import { dateFormatter } from '@vlabs/monokit/shared/ui/table-view/cell/formatters/date';
import { useHasPermission, usePageAccess } from '@vlabs/permissions';
import { Control } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';

import {
  titleCell, renderCell, renderHeader, createTimeCell, lastUpdateTimeCell,
  showCardsCell, ownerDepartmentCell, spacerCell, actionCell, dividerCell,
} from './cells';
import { ListListTableHead } from './ListListTableHead';
import { DeleteListsIconButton } from '../../buttons/DeleteListsIconButton';

interface ListsTableProps {
  data: List[];
  onSelect: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  selectedIds: Record<string, boolean>;
}

function DepartmentName({ departmentId }: { departmentId: DepartmentId }): React.JSX.Element {
  const { data: department } = useGetDepartment(departmentId);
  return <React.Fragment>{department?.name ?? departmentId}</React.Fragment>;
}

export function ListListTable({
  data,
  onSelect,
  selectedIds,
}: ListsTableProps): React.JSX.Element {
  const { t } = useTranslation();
  const canDeleteList = useHasPermission('list', 'delete');
  const canViewListDetails = usePageAccess('lists');

  const columns: ColumnDef<List>[] = useMemo(() => ([
    {
      accessorKey: 'user_data',
      header: renderHeader<string>(titleCell, t('list-list-page:table.titles.name')),
      cell: renderCell(titleCell),
    },
    {
      accessorKey: 'create_time',
      header: renderHeader<string>(createTimeCell, t('list-list-page:table.titles.created')),
      cell: renderCell(createTimeCell, (ctx) => dateFormatter(ctx.getValue())),
    },
    {
      accessorKey: 'last_update_time',
      header: renderHeader<string>(lastUpdateTimeCell, t('list-list-page:table.titles.last_update')),
      cell: renderCell(lastUpdateTimeCell, (ctx) => dateFormatter(ctx.getValue())),
    },
    ...(canViewListDetails ? [{
      accessorKey: 'list_id' as const,
      id: 'showCardsCount',
      header: renderHeader<string>(showCardsCell, t('list-list-page:table.titles.cards_in_list')),
      cell: renderCell(showCardsCell, (ctx) => (<ShowCardsCountButtonPoint3 listId={ctx.getValue()} />)),
    }] : []),
    {
      accessorKey: 'owner_department' as const,
      id: 'owner_department',
      header: renderHeader<string>(ownerDepartmentCell, t('list-list-page:table.titles.owner_department')),
      cell: renderCell(ownerDepartmentCell, (ctx) => {
        const departmentId = ctx.getValue();
        return <DepartmentName departmentId={departmentId} />;
      }),
    },
    { cell: spacerCell(undefined), id: 'spacer' },
    ...(canViewListDetails ? [{
      accessorKey: 'list_id' as const,
      id: 'viewListDetail',
      header: renderHeader(actionCell),
      cell: renderCell(actionCell, (ctx) => (<GoToListButton listId={ctx.getValue()} />)),
    }] : []),
    {
      accessorKey: 'list_id',
      id: 'copyList',
      header: renderHeader(actionCell),
      // FIXME Декоративная кнопка, заменить на фичу копирования списка
      cell: renderCell(actionCell, () => (
        <Control.IconButton
          disabled
          icon={<DuplicateIcon />}
          kind="secondary"
          shape="round"
          size="medium"
          variant="flat"
        />
      )),
    },
    ...(canDeleteList ? [{
      accessorKey: 'list_id' as const,
      id: 'deleteList',
      header: renderHeader(actionCell),
      cell: renderCell(actionCell, (ctx) => (<DeleteListsIconButton listIds={[ctx.getValue()]} />)),
    }] : []),
    { cell: dividerCell(undefined), id: 'divider' },
  ] as ColumnDef<List>[]), [t, canDeleteList, canViewListDetails]);

  let TableComponent: typeof TableView = TableView;
  if (canDeleteList) {
    TableComponent = TableViewSelectable as typeof TableView;
  }
  let selectProps = {};
  if (canDeleteList) {
    selectProps = {
      rowSelection: selectedIds,
      onRowSelectionChange: onSelect,
    };
  }

  return (
    <TableComponent<List>
      columns={columns}
      data={data}
      {...selectProps}
      getRowId={(row) => row.list_id}
      TableHead={ListListTableHead}
    />
  );
}
